/**
 * Fluxo de Caixa — redesign v1 (mock aprovado Veiga 23/06, docs/mock/
 * fluxo_redesign_v1.html). Inverte a hierarquia visual dos 2 graficos: o
 * SALDO projetado vira proagonista, o fluxo (entradas/saidas) vira contexto
 * secundario.
 *
 *   - <FluxoBarChart30d>: 2 paineis empilhados, mesmo dominio X —
 *       (a) SALDO HEROI: area preenchida (~65% da altura), gradient
 *           verde/vermelho cruzando zero (#129b/2, _calcZeroCrossingOffset),
 *           ReferenceLine de PISO_CAIXA_30D + zona de perigo sombreada,
 *           markers HOJE/6DU, marcador enfatico (anel coral, sem label) no
 *           ponto de menor saldo — o numero vive no KPI da page;
 *       (b) FAIXA DE FLUXO: barras divergentes pequenas (entrada teal pra
 *           cima, saida cinza pra baixo) — so contexto "o que moveu o saldo".
 *   - <FluxoBarChart6m>: WATERFALL — cada mes eh um degrau flutuante do saldo
 *       acumulado (verde net>0 / coral net<0), rotulo do saldo no topo,
 *       conectores tracejados, ReferenceLine de PISO_CAIXA_6M. Mantem a
 *       Posicao Aberta MtM dentro do cumulativo (fix #171).
 *
 * Historico do diagnostico (Veiga): os 2 graficos antigos misturavam FLUXO
 * (barra do dia/mes) e SALDO ACUMULADO (linha) no mesmo eixo Y, com o saldo
 * — a informacao que decide tudo — virando uma linha fininha perdida.
 *
 * Contrato de dados PRESERVADO (#155/#159/#171): os helpers _calc30d,
 * _calc30dFromSeries e _calc6m continuam identicos; o redesign eh puramente
 * de apresentacao. Data oficial do 6du vem SEMPRE de useProximo6du (prop
 * data6du) — heuristica local proibida (paridade #156/#158).
 *
 * FORA DE ESCOPO desta PR: toggle Nominal/Indexado do mock (pertence a frente
 * IPCA, precisa de dado indexado de fluxo que ainda nao existe). Quando entrar,
 * default = Indexado.
 */
import { useMemo } from "react";
import type { ReactNode } from "react";
import type { TooltipProps } from "recharts";
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { PontoFluxoDiario, PontoFluxoMensal } from "../../utils/mockData";
import type { FluxoDiaSerie, MarcaCessao } from "../../api/types";
import { Info } from "lucide-react";
import { fmtBRL } from "../../utils/formatters";
import { useSaldoV2 } from "../../hooks/useSaldoV2";
import { composicaoAncoraFluxo } from "./CompromissadaFluxoLinha";

// ---------- Paleta (identidade Cortex navy, tons do mock) ----------

const COR_SALDO_POS = "#74e08a"; // saldo projetado positivo (linha/area)
const COR_SALDO_NEG = "#f87171"; // saldo negativo (abaixo de zero)
const COR_ENTRADA = "#2cc4c8"; // faixa de fluxo — entradas (teal)
const COR_SAIDA = "#8B94A8"; // faixa de fluxo — saidas (cinza)
const COR_PISO = "#f0997b"; // piso de caixa + zona de perigo + marcador coral
const COR_WF_POS = "#3fae7a"; // degrau waterfall net>0
const COR_WF_NEG = "#d8704e"; // degrau waterfall net<0

// ---------- Pisos de caixa (configuraveis) ----------

// PROVISORIOS — e a TELA declara (label "provisório" na ReferenceLine +
// legenda). Nasceram placeholder no redesign #304 (23/06) e nunca foram
// validados; a decisao do numero e do VEIGA (07/08, #66 — nao passa pela
// Mesa). A zona de perigo e util mesmo aproximada; o que nao pode e o
// limiar se apresentar como oficial. Quando o Veiga cravar os numeros:
// atualizar os 2 valores E remover o sufixo "provisório" da tela junto.
export const PISO_CAIXA_30D = 800_000;
export const PISO_CAIXA_6M = 2_000_000;
export const PISO_PROVISORIO = true; // vira false no dia em que o Veiga cravar

// Largura fixa do YAxis nos 2 paineis do 30d. CRITICO: os 2 ResponsiveContainer
// empilhados precisam da MESMA largura de eixo Y + as mesmas margens horizontais
// pra o dominio X ficar alinhado pixel-a-pixel (saldo em cima, fluxo embaixo).
// 52 → 64: ticks ficaram ~2 chars mais largos com o sufixo "mi" do
// formatador unificado ("-R$ 12,5 mi") — 52px clipava negativo (review 03/07).
const YAXIS_W = 64;

export interface Row {
  periodo: string;
  // Legacy (6m): atacado/varejo sao ENTRADAS BRUTAS POR BU (pos-#157, dado
  // real). No 30d (#155+#159) ficam zerados — chart 30d nao mostra split BU.
  atacado: number;
  varejo: number;
  saidas: number; // armazenado negativo (Bar empilhado pra baixo)
  isSeisDu?: boolean;
  isHoje?: boolean;
  saldo_cumulativo: number;
  // Task #155: campos populados no path 30d (Omie-only pos-#155).
  // Ausentes no 6m legacy — discriminator usado pelo CustomTooltip pra
  // decidir formato (Atacado/Varejo vs Entradas/Saidas Omie).
  entradas_omie?: number;
  saidas_omie?: number; // negativo
  entradas_total?: number;
  saidas_total?: number; // negativo
  // #12 (visao completa): NET de energia (Zeus) do dia — entra SO na LINHA
  // de saldo (degrau), nunca nas barras (o lump do 6DU esmagaria o
  // operacional — a razao do #155). 0/ausente = dia sem energia.
  // fix #12b: nos 6DUs ja INCLUI a posicao aberta CCEE (bate o card).
  energia_net?: number;
  // fix #12b: componente Posicao Aberta CCEE (ESTIMATIVA, marca contra PLD
  // projetado) do energia_net — so nos 6DUs. Tooltip separa trade x posicao.
  posicao_aberta_ccee?: number;
  // 13/10 PAR CESSIONARIA: marcas do dia (so rotulo — o valor ja esta nas
  // saidas ou na energia; nunca entra no saldo).
  marcas_cessao?: MarcaCessao[];
}

// Task #155 + #12: series diarias consumidas pelo grafico Fluxo 30d. As
// BARRAS são operacionais (Omie CR / CP nao-energia); `energiaNet` (#12)
// entra APENAS na LINHA de saldo, como degraus por dt_pagamento — resolve o
// #155 sem re-esmagar as barras (a energia nao vira barra).
export interface Series30d {
  entradasOmie: FluxoDiaSerie[];
  saidasOmie: FluxoDiaSerie[];
  /** #12: NET de energia (Zeus venda−compra) por dia — pipeline do Calendario
   *  de Caixa (vigentes, exclusoes do card). Nos 6DUs JA inclui a posicao
   *  aberta CCEE (fix #12b — a linha bate o card "Saldo Total Estimado").
   *  Ausente → linha so operacional. */
  energiaNet?: FluxoDiaSerie[];
  /** fix #12b: componente Posicao Aberta CCEE (estimativa) por 6DU, SEGREGADO
   *  pro tooltip separar trade x posicao. Ja somado no energiaNet. */
  posicaoAberta6du?: FluxoDiaSerie[];
  /** 13/10 PAR CESSIONARIA: marcas por dia pro tooltip (parcela paga pelo
   *  recebivel cedido, sobra "cedida, destino nao confirmado"). So rotulo. */
  marcasCessao?: MarcaCessao[];
}

/** Rotulos de FONTE do tooltip 30d (call 08/09: o rotulo mentia a fonte).
 *  As duas barras vem de fontes DIFERENTES: entradas = espelho Omie
 *  (canonical_receita, por vencimento); saidas = CP vivo do Sync quando
 *  `cp_disponivel` (agregado por PREVISAO de pagamento, valor liquido) ou
 *  espelho Omie no fallback. O rotulo das saidas NUNCA e hardcodado aqui:
 *  vem de `cp_fonte_label` da response (o caller decide qual serie passou e
 *  passa o rotulo correspondente). `saidasEixo` diz o eixo de data usado,
 *  `saidasCaveat` repassa `cp_caveat` quando o backend mandar. */
export interface RotulosFonte30d {
  entradas: string;
  saidas: string;
  saidasEixo?: string | null;
  saidasCaveat?: string | null;
  /** Fonte "nua" (sem o prefixo Entradas/Saídas) pro SUBTITULO do card —
   *  a mesma verdade do tooltip, em uma linha de leitura de passagem. */
  entradasFonte: string;
  saidasFonte: string;
}

/** Default quando o caller nao informa: as duas series vieram do ESPELHO
 *  Omie (caminho legacy `diario` / fallback) — por vencimento. */
export const ROTULOS_ESPELHO_OMIE: RotulosFonte30d = {
  entradas: "Entradas · espelho Omie",
  saidas: "Saídas · espelho Omie",
  saidasEixo: "por vencimento",
  saidasCaveat: null,
  entradasFonte: "espelho Omie",
  saidasFonte: "espelho Omie",
};

/** Subtitulo do card 30d — UMA linha, mesma fonte de verdade do tooltip
 *  (fast-follow PR #411: o subtitulo fixo dizia "Omie CR/CP" tambem quando as
 *  barras vinham do CP vivo do Sync). Curto e correto > completo e ilegivel. */
export function subtituloFluxo30d(rot: RotulosFonte30d): string {
  const eixo = rot.saidasEixo ? `, ${rot.saidasEixo}` : "";
  return (
    `Saldo COMPLETO (operacional + energia) · barras: entradas ${rot.entradasFonte} · ` +
    `saídas ${rot.saidasFonte}${eixo} · energia entra na linha no 6DU e demais datas de pagamento`
  );
}

// ---------- Helpers de transformacao ----------

/** Converte PontoFluxoDiario[] -> Row[] e calcula saldo_cumulativo a partir
 *  de D+0 (primeiro elemento) ancorado em saldoInicial. Tudo forward —
 *  acabou o reverso pq janela comeca em HOJE.
 *
 *  Task #159: removido split sintetico 65/35 (era `atacado = entradas*0.65,
 *  varejo = entradas - atacado`) — caminho legacy/fallback agora trata
 *  entradas/saidas como agregado Omie unico, sem inventar BU. Row mantem
 *  `entradas_total`/`saidas_total` pra ativar o render multifonte do
 *  painel de fluxo (mesmo formato visual do caminho real).
 */
export function _calc30d(diario: PontoFluxoDiario[], saldoInicial: number): Row[] {
  let saldo = saldoInicial;
  return diario.map((p, idx) => {
    const entradasTotal = p.entradas;
    // Normaliza pra +0 (em vez de -0 por `-(0)`) — mesma convencao do FromSeries.
    const saidasTotal = p.saidas === 0 ? 0 : -p.saidas;
    saldo = saldo + entradasTotal + saidasTotal;
    return {
      periodo: `D+${idx}`,
      atacado: 0,
      varejo: 0,
      saidas: saidasTotal,
      isSeisDu: p.isSeisDu,
      isHoje: idx === 0,
      saldo_cumulativo: saldo,
      entradas_omie: entradasTotal,
      saidas_omie: saidasTotal,
      entradas_total: entradasTotal,
      saidas_total: saidasTotal,
      energia_net: 0, // fallback mock nao carrega energia (#12)
      posicao_aberta_ccee: 0,
    };
  });
}

/** Task #155: monta Row[] pra 31 dias [hoje..hoje+30] a partir das 2 series
 *  diarias Omie (CR/CP via canonical_*.data_vencimento). Para cada dia
 *  procura o ponto correspondente em cada serie (ausencia = 0), agrega
 *  entradas_total e saidas_total, e acumula saldo_cumulativo forward
 *  partindo de saldoInicial.
 *
 *  Pre-#155 somava tambem entradas/saidas Zeus — pico de R$ 9M+ do 6du
 *  achatava o fluxo Omie no eixo Y. Card "Proximo 6du" acima do grafico
 *  continua mostrando o valor Zeus consolidado; chart 30d ganha marker
 *  visual via ReferenceLine no dia do 6du.
 *
 *  - entradas_total = entradas_omie  (positivo)
 *  - saidas_total = -saidas_omie     (negativo, pra Bar empilhar pra baixo)
 *  - saldo_cumulativo[D] = saldo_cumulativo[D-1] + entradas_total - |saidas_total|
 *  - saldo_cumulativo[D+0] = saldoInicial + net_do_dia (mesma convencao do _calc30d legacy)
 */
export function _calc30dFromSeries(
  hojeIso: string,
  series: Series30d,
  saldoInicial: number,
  dias = 30,
): Row[] {
  const idx = (s: FluxoDiaSerie[]) => {
    const m = new Map<string, number>();
    for (const p of s) m.set(p.data, p.valor);
    return m;
  };
  const eomie = idx(series.entradasOmie);
  const somie = idx(series.saidasOmie);
  const enet = idx(series.energiaNet ?? []);
  const pos6du = idx(series.posicaoAberta6du ?? []);
  const marcasPorDia = new Map<string, MarcaCessao[]>();
  for (const m of series.marcasCessao ?? []) {
    marcasPorDia.set(m.data, [...(marcasPorDia.get(m.data) ?? []), m]);
  }

  // Itera dia a dia partindo de hoje. Construcao manual da date pra evitar
  // shift de timezone (new Date("YYYY-MM-DD") vira UTC midnight). `dias` = janela
  // do toggle (30/60/90): o loop vai de D+0 a D+dias.
  const [y, m, d] = hojeIso.split("-").map((n) => parseInt(n, 10));
  const base = new Date(y, m - 1, d);

  const rows: Row[] = [];
  let saldo = saldoInicial;
  for (let i = 0; i <= dias; i += 1) {
    const cur = new Date(base);
    cur.setDate(base.getDate() + i);
    const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;

    const eOmie = eomie.get(iso) ?? 0;
    const sOmie = somie.get(iso) ?? 0;
    const eNet = enet.get(iso) ?? 0;

    const entradasTotal = eOmie;
    // saidas armazenadas negativas pro Bar empilhar pra baixo do eixo.
    // Normaliza pra +0 (em vez de -0 por `-(0)`) — JS distingue, alguns
    // consumers (testes, alguns formatadores) tropeçam no Object.is(-0,0)=false.
    const saidasTotal = sOmie === 0 ? 0 : -sOmie;

    // #12 (visao completa): saldo[D] = saldo[D-1] + operacional + ENERGIA.
    // A energia entra SO aqui (degrau na linha) — as barras (entradas_total/
    // saidas_total) seguem operacionais, senao o lump do 6DU as esmagaria
    // (a razao do #155).
    saldo = saldo + entradasTotal + saidasTotal + eNet;

    rows.push({
      periodo: `D+${i}`,
      // Legacy fields atacado/varejo nao se aplicam no path 30d (#155);
      // 0 garante que Bars antigos (caso renderizem) nao apareçam.
      atacado: 0,
      varejo: 0,
      saidas: saidasTotal,
      isHoje: i === 0,
      saldo_cumulativo: saldo,
      entradas_omie: eOmie,
      saidas_omie: -sOmie,
      entradas_total: entradasTotal,
      saidas_total: saidasTotal,
      energia_net: eNet,
      posicao_aberta_ccee: pos6du.get(iso) ?? 0,
      marcas_cessao: marcasPorDia.get(iso) ?? [],
    });
  }
  return rows;
}

/** Seleciona a fonte de Row[] do 30d: series Omie (caminho real /api/v2/fluxo)
 *  ou o fallback mock (PontoFluxoDiario[] com a janela passada D-9..D-1 jogada
 *  fora via slice(9)). Centralizado pra o chart E a page (KPI "menor saldo")
 *  derivarem do MESMO array — sem divergencia. */
export function computeRows30d(
  series: Series30d | undefined,
  diario: PontoFluxoDiario[] | undefined,
  hojeIso: string | undefined,
  saldoInicial: number,
  dias = 30,
): Row[] {
  if (series) {
    const hoje = hojeIso ?? new Date().toISOString().slice(0, 10);
    return _calc30dFromSeries(hoje, series, saldoInicial, dias);
  }
  // Fallback mock (offline): a serie mock so cobre ~30d a frente — corta em
  // dias+1 pra nunca renderizar mais dias do que a janela pediu.
  return _calc30d((diario ?? []).slice(9, 9 + dias + 1), saldoInicial);
}

/** Converte PontoFluxoMensal[] -> Row[] forward acumulando saldo.
 *
 * Fix #171: inclui Posicao Aberta MtM no cumulativo pra alinhar com a
 * tabela "Saldo projetado 6 meses" (que ja inclui MtM desde Task #73).
 *
 * Fix #157: `saidas` agora vem do `m.total_saidas` BRUTO (= SUM(compras)
 * por mes) — antes era `-Math.round(m.total * 0.72)`, heuristica sintetica
 * que pintava saidas inventadas no chart 6m (mesmo bug que afetava a
 * Tabela 6m via projecaoSaldo6Meses). `atacado` e `varejo` agora
 * representam ENTRADAS brutas por BU (semantica explicitada no schema). */
export function _calc6m(
  mensal: PontoFluxoMensal[],
  saldoInicial: number,
  mtmByMonth?: Record<string, { mtm: number }>,
): Row[] {
  let saldo = saldoInicial;
  return mensal.map((m) => {
    const atacado = m.atacado;
    const varejo = m.varejo;
    const saidas = -m.total_saidas;
    const mtm = mtmByMonth?.[m.periodo]?.mtm ?? 0;
    saldo = saldo + atacado + varejo + saidas + mtm;
    return {
      periodo: m.periodo,
      atacado,
      varejo,
      saidas,
      saldo_cumulativo: saldo,
    };
  });
}

// ---------- Waterfall (6m) ----------

/** Degrau do waterfall mensal. Tecnica Recharts: barra empilhada com serie
 *  `base` transparente (= min(prev, cum)) + serie `delta` visivel (= |net|)
 *  colorida por sinal. `net` inclui a Posicao Aberta MtM (vem dobrado no
 *  saldo_cumulativo de _calc6m, fix #171). */
export interface WaterfallItem {
  periodo: string;
  base: number; // min(prev, cum) — empurra a barra visivel pra flutuar
  delta: number; // |net| — altura da barra visivel
  net: number; // cum - prev (com sinal; inclui MtM)
  cum: number; // saldo acumulado ao fim do mes
  prev: number; // saldo acumulado no inicio do mes
  positivo: boolean; // net >= 0 → degrau verde; senao coral
  bruto_entradas: number; // atacado + varejo (sem MtM) — label ↑
  bruto_saidas: number; // |saidas| (magnitude) — label ↓
}

/** Transforma Row[] (6m, ja com saldo_cumulativo acumulado) em degraus de
 *  waterfall. O `prev` do 1o mes eh o saldoInicial; dos demais eh o cum do
 *  mes anterior. Exportado pra teste (base + delta + sinal). */
export function _calcWaterfall(rows: Row[], saldoInicial: number): WaterfallItem[] {
  let prev = saldoInicial;
  return rows.map((r) => {
    const cum = r.saldo_cumulativo;
    const net = cum - prev;
    const item: WaterfallItem = {
      periodo: r.periodo,
      base: Math.min(prev, cum),
      delta: Math.abs(net),
      net,
      cum,
      prev,
      positivo: net >= 0,
      bruto_entradas: (r.atacado ?? 0) + (r.varejo ?? 0),
      bruto_saidas: Math.abs(r.saidas ?? 0),
    };
    prev = cum;
    return item;
  });
}

// ---------- Menor saldo projetado (30d) ----------

export interface MenorSaldo30d {
  valor: number;
  offsetLabel: string; // "D+N" do ponto de minimo
  index: number;
  periodo: string;
  abaixoPiso: boolean; // valor < piso → accent coral
}

/** Deriva o ponto de menor saldo cumulativo da serie 30d — alimenta o KPI
 *  "Menor saldo projetado (30d)" na page E a anotacao "menor saldo" no chart.
 *  Em empate pega o PRIMEIRO (offset mais cedo, leitura conservadora). Piso
 *  parametrizado pra teste; default = PISO_CAIXA_30D. */
export function _menorSaldo30d(
  rows: Row[],
  piso: number = PISO_CAIXA_30D,
): MenorSaldo30d | null {
  if (rows.length === 0) return null;
  let minIdx = 0;
  for (let i = 1; i < rows.length; i += 1) {
    if (rows[i].saldo_cumulativo < rows[minIdx].saldo_cumulativo) minIdx = i;
  }
  const r = rows[minIdx];
  return {
    valor: r.saldo_cumulativo,
    offsetLabel: r.periodo, // periodo ja eh "D+N"
    index: minIdx,
    periodo: r.periodo,
    abaixoPiso: r.saldo_cumulativo < piso,
  };
}

// ---------- Formatador pt-BR (Task #166) ----------

const _dtfBR = new Intl.DateTimeFormat("pt-BR", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export function _formatDateBR(date: Date): string {
  return _dtfBR.format(date);
}

function _resolveDate(label: unknown, hojeIso: string | undefined): string | null {
  if (typeof label !== "string") return null;
  const m = /^D\+(\d+)$/.exec(label);
  if (!m) return null;
  const offset = parseInt(m[1], 10);
  let base: Date;
  if (hojeIso) {
    const [y, mo, d] = hojeIso.split("-").map((n) => parseInt(n, 10));
    base = new Date(y, mo - 1, d);
  } else {
    const n = new Date();
    base = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }
  base.setDate(base.getDate() + offset);
  return _formatDateBR(base);
}

/** Compacto em milhoes pt-BR sem "R$" pros labels ↑/↓ do waterfall (mock:
 *  "↑11,7 ↓10,9"). 1 casa decimal. */
function _fmtMi(v: number): string {
  return (v / 1_000_000).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

// ---------- CustomTooltip 30d (mantido do PR #109/#155) ----------

type TooltipEntry = NonNullable<TooltipProps<number, string>["payload"]>[number];

function _findValue(payload: readonly TooltipEntry[] | undefined, key: string): number {
  if (!payload) return 0;
  const e = payload.find((p) => p.dataKey === key);
  const v = e?.value;
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

export function CustomTooltip({
  active,
  payload,
  label,
  hojeIso,
  rotulos,
}: TooltipProps<number, string> & { hojeIso?: string; rotulos?: RotulosFonte30d }) {
  if (!active || !payload || payload.length === 0) return null;

  // Task #155: discriminator 30d-vs-6m vem do Row inteiro em
  // payload[0].payload — 30d (Omie-only pos-#155) popula entradas_omie/
  // saidas_omie; 6m usa o _WaterfallTooltip dedicado. NAO ha branch Zeus —
  // pico do 6du foi removido do dataset 30d, valor fica no Card "Proximo 6du".
  const rowAny = payload[0]?.payload as Record<string, unknown> | undefined;
  const eOmie = rowAny?.entradas_omie as number | undefined;
  const sOmie = rowAny?.saidas_omie as number | undefined;
  const isOmie30d = eOmie !== undefined || sOmie !== undefined;

  const saldoEntry = payload.find((p) => p.dataKey === "saldo_cumulativo");
  const saldo = typeof saldoEntry?.value === "number" ? saldoEntry.value : null;
  const saldoNegativo = saldo !== null && saldo < 0;

  if (isOmie30d) {
    const eo = eOmie ?? 0;
    const so = Math.abs(sOmie ?? 0);
    // #12: energia do dia SEPARADA do operacional no tooltip. Dia sem energia
    // (0/ausente) → linha omitida (so operacional).
    // fix #12b: no 6DU o energia_net inclui a posicao aberta CCEE — o tooltip
    // SEPARA "Energia trade (Zeus)" (net de trades) da "Posicao aberta CCEE
    // (estimativa)" (marca contra PLD projetado). Dia sem posicao → so trade.
    const eNet = (rowAny?.energia_net as number | undefined) ?? 0;
    const posCcee = (rowAny?.posicao_aberta_ccee as number | undefined) ?? 0;
    const eTrade = eNet - posCcee;
    const marcasCessao = (rowAny?.marcas_cessao as MarcaCessao[] | undefined) ?? [];
    const dataBR = _resolveDate(label, hojeIso);
    const rot = rotulos ?? ROTULOS_ESPELHO_OMIE;
    return (
      <div
        className="bg-indra-azul-deep/95 border border-indra-turquesa/40 rounded-lg p-3 text-xs shadow-xl backdrop-blur-sm min-w-[240px]"
        data-testid="fluxo-tooltip"
      >
        <div className="font-semibold text-white mb-2 text-[13px]">{dataBR ?? label}</div>

        <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 font-mono tabular-nums">
          <span className="text-indra-turquesa" data-testid="fluxo-tooltip-entradas-label">
            {rot.entradas}
          </span>
          <span className="text-right text-white/90" data-testid="fluxo-tooltip-entradas">
            {fmtBRL(eo, { abbreviate: true })}
          </span>
          <span className="text-[#FF6B6B]" data-testid="fluxo-tooltip-saidas-label">
            {rot.saidas}
          </span>
          <span className="text-right text-white/90" data-testid="fluxo-tooltip-saidas">
            {fmtBRL(so, { abbreviate: true })}
          </span>
          {/* #154 (10/09): o tooltip e superficie de UM PONTO — data + valores, e
              so. O eixo das saidas ("por previsao de pagamento") e o cp_caveat sao
              fatos da SERIE e moram no subtitulo/aviso do card (_CardShell), nunca
              aqui: o caveat inteiro dentro do tooltip virou um card de ~8 linhas
              que TAPAVA o grafico ("nao consigo ver em qual data estou clicando"). */}
          {eTrade !== 0 && (
            <>
              <span style={{ color: COR_SALDO_POS }} data-testid="fluxo-tooltip-energia-label">
                {posCcee !== 0 ? "Energia trade (Zeus)" : "Energia (Zeus)"}
              </span>
              <span className="text-right text-white/90" data-testid="fluxo-tooltip-energia">
                {(eTrade >= 0 ? "+" : "−") + fmtBRL(Math.abs(eTrade), { abbreviate: true })}
              </span>
            </>
          )}
          {posCcee !== 0 && (
            <>
              <span style={{ color: COR_SALDO_POS }} data-testid="fluxo-tooltip-posicao-label">
                Posição aberta CCEE (estimativa)
              </span>
              <span className="text-right text-white/90" data-testid="fluxo-tooltip-posicao">
                {(posCcee >= 0 ? "+" : "−") + fmtBRL(Math.abs(posCcee), { abbreviate: true })}
              </span>
            </>
          )}
        </div>

        {/* 13/10 PAR CESSIONARIA: o valor ja esta nas linhas acima (saida ou
            energia) — aqui so se diz de onde vem / para onde vai. */}
        {marcasCessao.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-[11px] text-white/70" data-testid="fluxo-tooltip-cessao">
            {marcasCessao.map((m, i) => (
              <li key={i}>
                {fmtBRL(m.valor, { abbreviate: true })} · {m.rotulo}
              </li>
            ))}
          </ul>
        )}

        {saldo !== null && (
          <div
            className="border-t border-white/15 mt-2 pt-2 flex justify-between gap-4 font-mono tabular-nums"
            data-testid="fluxo-tooltip-saldo"
          >
            <span className="text-white/70 text-[11px] uppercase tracking-wider">
              Saldo projetado
            </span>
            <span
              className={
                "font-semibold " +
                (saldoNegativo ? "text-[#FF6B6B]" : "text-indra-turquesa")
              }
            >
              {fmtBRL(saldo, { abbreviate: true })}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Legacy 6m (Atacado/Varejo BU brutos pos-#157) — preservado pra cobertura
  // de teste do CustomTooltip; o chart 6m em si usa _WaterfallTooltip.
  const atacado = _findValue(payload, "atacado");
  const varejo = _findValue(payload, "varejo");
  const saidas = _findValue(payload, "saidas");
  const totalEntradas = atacado + varejo;
  const saidasAbs = Math.abs(saidas);

  return (
    <div
      className="bg-indra-azul-deep/95 border border-indra-turquesa/40 rounded-lg p-3 text-xs shadow-xl backdrop-blur-sm"
      data-testid="fluxo-tooltip"
    >
      <div className="font-semibold text-white mb-2 text-[13px]">{label}</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono tabular-nums">
        <span className="text-[#00DAC8]">Atacado</span>
        <span className="text-right text-white/90">{fmtBRL(atacado, { abbreviate: true })}</span>
        <span className="text-[#4FB3E8]">Varejo</span>
        <span className="text-right text-white/90">{fmtBRL(varejo, { abbreviate: true })}</span>
        <span className="text-white/60 pl-3">↳ Entradas</span>
        <span className="text-right text-white/70">{fmtBRL(totalEntradas, { abbreviate: true })}</span>
        <span className="text-[#FF6B6B]">Saídas</span>
        <span className="text-right text-white/90">{fmtBRL(saidasAbs, { abbreviate: true })}</span>
      </div>
      {saldo !== null && (
        <div
          className="border-t border-white/15 mt-2 pt-2 flex justify-between gap-4 font-mono tabular-nums"
          data-testid="fluxo-tooltip-saldo"
        >
          <span className="text-white/70 text-[11px] uppercase tracking-wider">
            Saldo cumulativo
          </span>
          <span
            className={
              "font-semibold " +
              (saldoNegativo ? "text-[#FF6B6B]" : "text-indra-turquesa")
            }
          >
            {fmtBRL(saldo, { abbreviate: true })}
          </span>
        </div>
      )}
    </div>
  );
}

/** Tooltip do waterfall 6m — le o WaterfallItem inteiro em payload[0].payload
 *  (entradas/saidas brutas, net do mes, saldo acumulado). */
function _WaterfallTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload as WaterfallItem | undefined;
  if (!row) return null;
  const saldoNeg = row.cum < 0;
  const netTxt = (row.net >= 0 ? "+" : "−") + fmtBRL(Math.abs(row.net), { abbreviate: true });
  return (
    <div
      className="bg-indra-azul-deep/95 border border-indra-turquesa/40 rounded-lg p-3 text-xs shadow-xl backdrop-blur-sm min-w-[220px]"
      data-testid="fluxo-tooltip"
    >
      <div className="font-semibold text-white mb-2 text-[13px]">{row.periodo}</div>
      <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 font-mono tabular-nums">
        <span className="text-[#2cc4c8]">Entradas</span>
        <span className="text-right text-white/90">{fmtBRL(row.bruto_entradas, { abbreviate: true })}</span>
        <span className="text-[#8B94A8]">Saídas</span>
        <span className="text-right text-white/90">{fmtBRL(row.bruto_saidas, { abbreviate: true })}</span>
        <span className={row.positivo ? "text-[#3fae7a]" : "text-[#d8704e]"}>Net do mês</span>
        <span className="text-right text-white/90">{netTxt}</span>
      </div>
      <div
        className="border-t border-white/15 mt-2 pt-2 flex justify-between gap-4 font-mono tabular-nums"
        data-testid="fluxo-tooltip-saldo"
      >
        <span className="text-white/70 text-[11px] uppercase tracking-wider">Saldo acumulado</span>
        <span className={"font-semibold " + (saldoNeg ? "text-[#FF6B6B]" : "text-indra-turquesa")}>
          {fmtBRL(row.cum, { abbreviate: true })}
        </span>
      </div>
    </div>
  );
}

// ---------- Markers / offsets (mantidos #155, #129b/2) ----------

/** Task #155: calcula o offset "D+N" do marker 6du dada a data oficial e
 *  o ancora hoje. Retorna null quando data6du eh nullish ou cai fora da
 *  janela [D+0, D+dias] (marker some). `dias` = janela do toggle (30/60/90),
 *  default 30. Exportado pra teste.
 */
export function _offset6du(
  data6du: Date | null | undefined,
  hojeIso: string | undefined,
  dias = 30,
): number | null {
  if (!data6du) return null;
  let base: Date;
  if (hojeIso) {
    const [y, m, d] = hojeIso.split("-").map((n) => parseInt(n, 10));
    base = new Date(y, m - 1, d);
  } else {
    const n = new Date();
    base = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }
  const alvo = new Date(
    data6du.getFullYear(),
    data6du.getMonth(),
    data6du.getDate(),
  );
  const diff = Math.round((alvo.getTime() - base.getTime()) / 86_400_000);
  if (diff < 0 || diff > dias) return null;
  return diff;
}

/** Mapeia uma LISTA de datas 6DU (calendario CCEE oficial — useCalendario6dus)
 *  pros offsets D+N dentro da janela [D+0, D+dias]. Filtra as que caem fora,
 *  deduplica por offset e ordena. Usado pra renderizar TODOS os 6DUs visiveis
 *  na janela (30 mostra ~1, 90 pode mostrar 3+) — nunca calcula data localmente
 *  (regra de ferro #156/#158: a serie de 6dus vem do backend). Exportado p/ teste. */
export function _offsets6du(
  datas: ReadonlyArray<Date | null | undefined> | undefined,
  hojeIso: string | undefined,
  dias = 30,
): { offset: number; iso: string }[] {
  if (!datas || datas.length === 0) return [];
  const seen = new Set<number>();
  const out: { offset: number; iso: string }[] = [];
  for (const d of datas) {
    const off = _offset6du(d, hojeIso, dias);
    if (off === null || seen.has(off)) continue;
    seen.add(off);
    out.push({ offset: off, iso: _resolveDate(`D+${off}`, hojeIso) ?? `D+${off}` });
  }
  return out.sort((a, b) => a.offset - b.offset);
}

/** Calcula o offset (0..1) onde y=0 cai dentro da escala do saldo. Retorna
 *  null quando todos os pontos estao do mesmo lado (caller usa cor solida).
 *  Exportado pra teste.
 */
export function _calcZeroCrossingOffset(rows: Row[]): number | null {
  if (rows.length === 0) return null;
  const dataMin = Math.min(...rows.map((r) => r.saldo_cumulativo));
  const dataMax = Math.max(...rows.map((r) => r.saldo_cumulativo));
  if (dataMin >= 0 || dataMax <= 0) return null;
  // y cresce de cima pra baixo no SVG; offset 0 = topo (max), 1 = base (min).
  // Zero cai a `dataMax / (dataMax - dataMin)` de cima.
  return dataMax / (dataMax - dataMin);
}

// ---------- Card shell + legendas ----------

function _Swatch({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center gap-1.5">
      {children}
    </li>
  );
}

/** Legenda do 30d (mock): Saldo projetado (linha verde), Entradas (teal),
 *  Saidas (cinza), Piso de caixa (tracejado coral). */
function _Legenda30d() {
  return (
    <ul
      className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-3 px-1"
      data-testid="fluxo-legenda"
    >
      <_Swatch>
        <span aria-hidden className="inline-block w-3.5 h-[3px] rounded" style={{ background: COR_SALDO_POS }} />
        <span className="text-[11px] text-white/70">Saldo completo (oper. + energia)</span>
      </_Swatch>
      <_Swatch>
        <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COR_ENTRADA }} />
        <span className="text-[11px] text-white/70">Entradas (oper.)</span>
      </_Swatch>
      <_Swatch>
        <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COR_SAIDA }} />
        <span className="text-[11px] text-white/70">Saídas (oper.)</span>
      </_Swatch>
      <_Swatch>
        <span
          aria-hidden
          className="inline-block w-3.5 h-0"
          style={{ borderTop: `2px dashed ${COR_PISO}` }}
        />
        <span className="text-[11px] text-white/70">
          Piso de caixa{PISO_PROVISORIO ? " (provisório)" : ""}
        </span>
      </_Swatch>
    </ul>
  );
}

/** Legenda do waterfall 6m: Mes positivo (verde), Mes negativo (coral),
 *  Piso de caixa (tracejado coral). */
function _Legenda6m() {
  return (
    <ul
      className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-3 px-1"
      data-testid="fluxo-legenda-6m"
    >
      <_Swatch>
        <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COR_WF_POS }} />
        <span className="text-[11px] text-white/70">Mês positivo</span>
      </_Swatch>
      <_Swatch>
        <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COR_WF_NEG }} />
        <span className="text-[11px] text-white/70">Mês negativo</span>
      </_Swatch>
      <_Swatch>
        <span
          aria-hidden
          className="inline-block w-3.5 h-0"
          style={{ borderTop: `2px dashed ${COR_PISO}` }}
        />
        <span className="text-[11px] text-white/70">
          Piso de caixa{PISO_PROVISORIO ? " (provisório)" : ""}
        </span>
      </_Swatch>
    </ul>
  );
}

/** #149 — a MARCA DE SUBESTIMACAO, nivel SERIE: uma linha sempre visivel dizendo o
 *  SENTIDO do erro (saidas incompletas -> saldo otimista), com o texto completo do
 *  backend (`cp_caveat`) no hover do icone. Sem percentual: ninguem mediu de quanto
 *  (pergunta ao Sync, nao numero nosso). Nao e banner, nao e modal, nao e tooltip. */
export const AVISO_SUBESTIMACAO =
  "Saídas incompletas (NFe e lançamentos diretos de CC não entram) → saldo projetado OTIMISTA";

function _CardShell({
  titulo,
  subtitulo,
  aviso,
  legenda,
  children,
}: {
  titulo: string;
  subtitulo: string;
  /** texto completo (cp_caveat do backend) exibido no hover/click do icone;
   *  null/undefined = serie sem caveat declarado -> a linha nao aparece. */
  aviso?: string | null;
  legenda: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="text-white font-semibold text-sm">{titulo}</h3>
        <span
          className="text-[11px] text-white/40 uppercase tracking-wider"
          data-testid="fluxo-card-subtitulo"
        >
          {subtitulo}
        </span>
      </div>
      {aviso && (
        <div
          className="flex items-center gap-1.5 mb-3 text-[11px] text-amber-200/85"
          data-testid="fluxo-card-aviso-subestimacao"
        >
          <span className="cursor-help inline-flex" title={aviso} data-testid="fluxo-card-aviso-info">
            <Info size={12} className="text-amber-200/80" />
          </span>
          <span>{AVISO_SUBESTIMACAO}</span>
        </div>
      )}
      {!aviso && <div className="mb-2" />}
      {children}
      {legenda}
    </div>
  );
}

// ---------- Painel 30d (saldo heroi + faixa de fluxo) ----------

function _Card30d({
  rows,
  data6du,
  datas6du,
  hojeIso,
  dias = 30,
  rotulos,
}: {
  rows: Row[];
  data6du: Date | null;
  /** Task: lista de 6DUs da janela (calendario CCEE). Quando presente e
   *  nao-vazia, renderiza UM marcador por 6DU dentro de [D+0, D+dias]; senao
   *  cai no `data6du` unico (compat com chamadas legadas). */
  datas6du?: ReadonlyArray<Date | null | undefined>;
  hojeIso?: string;
  dias?: number;
  /** Rotulos de fonte do tooltip (ver RotulosFonte30d). */
  rotulos?: RotulosFonte30d;
}) {
  const piso = PISO_CAIXA_30D;

  // Task #129b/2: cor do saldo segue gradient vertical verde/vermelho cruzando
  // o zero. Mesmo lado → cor solida (gradient de 1 stop nao vale a pena).
  const zeroOffset = _calcZeroCrossingOffset(rows);
  const todosNegativos = rows.length > 0 && rows.every((r) => r.saldo_cumulativo < 0);
  const usarGradient = zeroOffset !== null;
  const corSolida = todosNegativos ? COR_SALDO_NEG : COR_SALDO_POS;
  const gradientId = "fluxo-saldo-gradient-30d";
  const strokeLinha = usarGradient ? `url(#${gradientId})` : corSolida;

  // Markers dos 6DUs na janela (data oficial CCEE; nunca calcular local —
  // #156/#158). Com `datas6du` mostra TODAS as liquidacoes do range (30 mostra
  // ~1, 60/90 podem mostrar 2-3); senao um marcador unico via `data6du`.
  const markers6du =
    datas6du && datas6du.length > 0
      ? _offsets6du(datas6du, hojeIso, dias)
      : _offsets6du(data6du ? [data6du] : [], hojeIso, dias);

  // Densidade de ticks no eixo X: janela maior espaca mais (evita aglomerar
  // labels em 60/90d). ~10-12 ticks visiveis em qualquer janela.
  const xInterval = dias <= 30 ? 2 : dias <= 60 ? 4 : 6;

  // Empty state HONESTO — so quando TUDO esta vazio: operacional (CR/CP) E
  // energia (#12). Com energia na janela, a linha completa renderiza mesmo
  // sem movimento operacional.
  const semMovimentacao =
    rows.length > 0 &&
    rows.every(
      (r) =>
        (r.entradas_total ?? 0) === 0 &&
        (r.saidas_total ?? 0) === 0 &&
        (r.energia_net ?? 0) === 0,
    );

  if (semMovimentacao) {
    return (
      <div data-testid="fluxo-chart-30d">
        <div
          className="h-[300px] flex items-center justify-center text-white/40 text-sm text-center px-6"
          data-testid="fluxo-chart-30d-empty"
        >
          Sem movimentação prevista nos próximos {dias} dias.
        </div>
      </div>
    );
  }

  const saldos = rows.map((r) => r.saldo_cumulativo);
  // Dominio do painel do saldo: inclui 0, o piso e folga no topo. yMin define
  // a base da zona de perigo (ReferenceArea abaixo do piso).
  const yMin = Math.min(0, ...saldos);
  const yMaxRaw = Math.max(piso, ...saldos);
  const yMax = yMaxRaw + Math.max(Math.abs(yMaxRaw) * 0.1, 1);

  const menor = _menorSaldo30d(rows, piso);

  return (
    <div data-testid="fluxo-chart-30d">
      {/* (a) SALDO HEROI — area preenchida ocupando o topo do card */}
      <div data-testid="fluxo-chart-30d-saldo" style={{ height: 210 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 26, right: 16, left: 0, bottom: 0 }}
            syncId="fluxo-30d"
          >
            {usarGradient && (
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  data-testid="fluxo-chart-30d-gradient"
                >
                  <stop offset="0%" stopColor={COR_SALDO_POS} stopOpacity={0.95} />
                  <stop offset={`${zeroOffset! * 100}%`} stopColor={COR_SALDO_POS} stopOpacity={0.95} />
                  <stop offset={`${zeroOffset! * 100}%`} stopColor={COR_SALDO_NEG} stopOpacity={0.95} />
                  <stop offset="100%" stopColor={COR_SALDO_NEG} stopOpacity={0.95} />
                </linearGradient>
              </defs>
            )}
            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
            {/* XAxis escondido no painel de cima — os ticks D+N ficam no painel
                de baixo. Mantido pra o dominio X casar com a faixa de fluxo. */}
            <XAxis dataKey="periodo" hide />
            <YAxis
              width={YAXIS_W}
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              domain={[yMin, yMax]}
              tickFormatter={(v) => fmtBRL(v, { abbreviate: true })}
            />
            <Tooltip
              cursor={{ stroke: "rgba(255,255,255,0.25)", strokeDasharray: "3 3" }}
              content={<CustomTooltip hojeIso={hojeIso} rotulos={rotulos} />}
            />
            {/* Zona de perigo: faixa sombreada abaixo do piso */}
            <ReferenceArea
              y1={piso}
              y2={yMin}
              fill={COR_PISO}
              fillOpacity={0.06}
              ifOverflow="extendDomain"
            />
            <ReferenceLine
              y={piso}
              stroke={COR_PISO}
              strokeWidth={1}
              strokeDasharray="4 4"
              strokeOpacity={0.75}
              label={{
                value: `piso ${fmtBRL(piso, { abbreviate: true })}${PISO_PROVISORIO ? " · provisório (a definir)" : ""}`,
                position: "insideTopLeft",
                fill: COR_PISO,
                fontSize: 10,
              }}
            />
            <Area
              type="monotone"
              dataKey="saldo_cumulativo"
              name="Saldo projetado"
              stroke={strokeLinha}
              strokeWidth={2.5}
              fill="rgba(116,224,138,0.10)"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
              connectNulls
            />
            <ReferenceLine
              x="D+0"
              stroke="#FFFFFF"
              strokeOpacity={0.55}
              strokeDasharray="3 3"
              label={{
                value: "HOJE",
                position: "top",
                // Desloca pra dentro/baixo pra nao encostar no tick do YAxis
                // (ex.: "R$ 4,5M") no canto superior esquerdo.
                dx: 14,
                dy: 6,
                fill: "#FFFFFF",
                fontSize: 11,
                fontWeight: 600,
              }}
            />
            {markers6du.map((mk) => (
              <ReferenceLine
                key={`6du-${mk.offset}`}
                x={`D+${mk.offset}`}
                stroke="rgba(255,255,255,0.55)"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{
                  value: mk.iso ? `Liquidação CCEE — ${mk.iso}` : "6DU",
                  position: "top",
                  fill: "rgba(255,255,255,0.7)",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            ))}
            {/* Marcador enfatico do ponto de minimo — anel coral, SEM label de
                texto: o numero vive no KPI "Menor saldo projetado (30d)" no topo
                da page (evita colidir com piso/borda direita). */}
            {menor && (
              <ReferenceDot
                x={menor.periodo}
                y={menor.valor}
                r={4.5}
                fill={COR_PISO}
                stroke="#FFFFFF"
                strokeWidth={1.5}
                ifOverflow="extendDomain"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* (b) FAIXA DE FLUXO — barras divergentes pequenas, mesmo dominio X */}
      <div data-testid="fluxo-chart-30d-fluxo" style={{ height: 96 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
            syncId="fluxo-30d"
            stackOffset="sign"
          >
            <XAxis
              dataKey="periodo"
              stroke="rgba(255,255,255,0.5)"
              fontSize={10}
              interval={xInterval}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            />
            {/* YAxis invisivel SO pra reservar a mesma largura do painel de cima
                (alinhamento X pixel-a-pixel). */}
            <YAxis width={YAXIS_W} tick={false} axisLine={false} tickLine={false} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.12)" />
            <Bar
              dataKey="entradas_total"
              name="Entradas"
              stackId="fluxo"
              fill={COR_ENTRADA}
              barSize={7}
              radius={[1, 1, 0, 0]}
              isAnimationActive={false}
            />
            <Bar
              dataKey="saidas_total"
              name="Saídas"
              stackId="fluxo"
              fill={COR_SAIDA}
              barSize={7}
              radius={[0, 0, 1, 1]}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------- Painel 6m (waterfall) ----------

/** Tick custom do XAxis 6m: 1a linha = mes (Jul/26), 2a linha = bruto
 *  (↑entradas ↓saidas). grossMap injetado via cloneElement (Recharts passa
 *  x/y/payload). */
function _TickMes(props: {
  x?: number;
  y?: number;
  payload?: { value?: string };
  grossMap?: Record<string, { entradas: number; saidas: number }>;
}) {
  const { x = 0, y = 0, payload, grossMap } = props;
  const periodo = payload?.value ?? "";
  const g = grossMap?.[periodo];
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={14} textAnchor="middle" fontSize={12} fill="#cfd9e6">
        {periodo}
      </text>
      {g && (
        <text x={0} y={0} dy={29} textAnchor="middle" fontSize={10} fill="#5e7088">
          {`↑${_fmtMi(g.entradas)} ↓${_fmtMi(g.saidas)}`}
        </text>
      )}
    </g>
  );
}

// Props que o Recharts injeta no `content` do LabelList — x/y/width/value
// podem chegar como string|number; coercimos pra number nos calculos.
interface LabelContentProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  value?: number | string;
}

/** Rotulo do SALDO ACUMULADO no topo de cada degrau. */
function _renderCumLabel(props: LabelContentProps) {
  const { x, y, width, value } = props;
  if (x == null || y == null) return null;
  const cx = Number(x) + Number(width ?? 0) / 2;
  return (
    <text x={cx} y={Number(y) - 6} textAnchor="middle" fontSize={12} fontWeight={500} fill="#e9eef5">
      {fmtBRL(Number(value), { abbreviate: true })}
    </text>
  );
}

/** Rotulo discreto do NET do mes (com sinal, colorido) embaixo do degrau. */
function _renderNetLabel(props: LabelContentProps) {
  const { x, y, width, value } = props;
  if (x == null || y == null) return null;
  const net = Number(value);
  const cx = Number(x) + Number(width ?? 0) / 2;
  const txt = (net >= 0 ? "+" : "−") + fmtBRL(Math.abs(net), { abbreviate: true });
  return (
    <text x={cx} y={Number(y) + 14} textAnchor="middle" fontSize={10.5} fill={net >= 0 ? COR_WF_POS : COR_WF_NEG}>
      {txt}
    </text>
  );
}

function _Card6m({ rows, saldoInicial }: { rows: Row[]; saldoInicial: number }) {
  const items = useMemo(() => _calcWaterfall(rows, saldoInicial), [rows, saldoInicial]);
  const piso = PISO_CAIXA_6M;

  const grossMap = useMemo(() => {
    const out: Record<string, { entradas: number; saidas: number }> = {};
    for (const it of items) {
      out[it.periodo] = { entradas: it.bruto_entradas, saidas: it.bruto_saidas };
    }
    return out;
  }, [items]);

  const bases = items.map((i) => i.base);
  const tops = items.map((i) => i.base + i.delta);
  const yMin = Math.min(0, ...bases);
  const yMaxRaw = Math.max(piso, ...tops);
  const yMax = yMaxRaw + Math.max(Math.abs(yMaxRaw) * 0.14, 1);

  return (
    <div data-testid="fluxo-chart-6m" style={{ height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={items} margin={{ top: 28, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
          <XAxis
            dataKey="periodo"
            interval={0}
            height={44}
            tickLine={false}
            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            tick={<_TickMes grossMap={grossMap} />}
          />
          <YAxis
            width={YAXIS_W}
            stroke="rgba(255,255,255,0.5)"
            fontSize={11}
            tickLine={false}
            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            domain={[yMin, yMax]}
            tickFormatter={(v) => fmtBRL(v, { abbreviate: true })}
          />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<_WaterfallTooltip />} />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.18)" />
          <ReferenceLine
            y={piso}
            stroke={COR_PISO}
            strokeWidth={1}
            strokeDasharray="4 4"
            strokeOpacity={0.55}
            label={{
              value: `piso ${fmtBRL(piso, { abbreviate: true })}${PISO_PROVISORIO ? " · provisório (a definir)" : ""}`,
              position: "insideTopRight",
              fill: COR_PISO,
              fontSize: 10,
            }}
          />
          {/* Conectores tracejados entre degraus (cum do mes i → inicio do mes i+1) */}
          {items.slice(0, -1).map((it, i) => (
            <ReferenceLine
              key={`conn-${it.periodo}`}
              stroke="rgba(255,255,255,0.25)"
              strokeDasharray="3 3"
              segment={[
                { x: it.periodo, y: it.cum },
                { x: items[i + 1].periodo, y: it.cum },
              ]}
            />
          ))}
          {/* Base transparente empurra a barra visivel pra flutuar */}
          <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="delta" stackId="wf" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {items.map((it) => (
              <Cell key={`wf-${it.periodo}`} fill={it.positivo ? COR_WF_POS : COR_WF_NEG} />
            ))}
            <LabelList dataKey="cum" position="top" content={_renderCumLabel} />
            <LabelList dataKey="net" position="bottom" content={_renderNetLabel} />
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Componentes publicos ----------

interface Props30d {
  /** Legacy fallback (mock PontoFluxoDiario[]) — usado apenas quando
   *  `series` nao chega (ainda carregando ou rota /api/v2/fluxo offline).
   *  Pos-#155, esse caminho tambem agrega Omie-only (sem split 65/35).
   */
  diario?: PontoFluxoDiario[];
  /** Task #155: 2 series diarias Omie (CR/CP) 30d vindas de
   *  /api/v2/fluxo. Zeus removido do dataset — pico do 6du achatava
   *  visualmente o fluxo Omie diario. Quando presente, sobrescreve o
   *  caminho legacy `diario`.
   */
  series?: Series30d;
  /** Rotulos de FONTE das barras no tooltip. O caller que escolheu a serie
   *  (saidas_cp_dia x saidas_omie_dia) passa o rotulo que a response deu
   *  (`cp_fonte_label`) — nunca uma string fixa aqui. Ausente → espelho Omie. */
  rotulos?: RotulosFonte30d;
  /** ISO YYYY-MM-DD do dia que ancora D+0 — vem de fluxoV2.data.hoje
   *  (backend usa date.today() ou Query param). Default: hoje local. */
  hojeIso?: string;
  /** Task #155: data oficial CCEE do proximo 6du (de useProximo6du,
   *  resolvida no caller). Renderiza ReferenceLine "6DU" no eixo X
   *  quando cai dentro de [D+0, D+dias]. Heuristica local proibida —
   *  paridade com #156/#158. Usado quando `datas6du` nao chega. */
  data6du?: Date | null;
  /** Lista de 6DUs da janela navegavel (calendario CCEE — useCalendario6dus).
   *  Quando presente, renderiza UM marcador por 6DU dentro de [D+0, D+dias]
   *  (em 60/90d aparecem 2-3 liquidacoes). Datas SEMPRE do backend (nunca
   *  calculadas localmente — regra de ferro #156/#158). */
  datas6du?: ReadonlyArray<Date | null | undefined>;
  /** Janela do grafico em dias (toggle 30/60/90). Default 30. Escala o eixo X
   *  (D+0..D+dias), a serie do saldo e o range dos marcadores 6DU. */
  dias?: number;
  /** Saldo de partida (D+0). Quando o caller ja resolveu o saldo (page usa
   *  saldoV2 com fallback mock), passa aqui pra o grafico E o KPI "menor
   *  saldo" derivarem da MESMA base — senao o grafico cairia no `?? 0`
   *  interno e divergiria do KPI da page numa falha do /api/v2/saldo. Omitido
   *  (testes) → cai no useSaldoV2 interno. */
  saldoInicial?: number;
}

/** Grafico 30 dias — SALDO HEROI (area) + faixa de fluxo (barras pequenas).
 *
 *  Task #155: consome 2 series diarias Omie (CR/CP) — Zeus removido pq o
 *  pico de R$ 9M+ do 6du achatava visualmente o fluxo operacional. Card
 *  "Proximo 6du" acima mostra o valor Zeus consolidado; aqui um marker "6DU"
 *  no eixo X marca o dia. Fallback `diario` cai no mesmo formato Omie-only.
 */
export function FluxoBarChart30d({
  diario,
  series,
  rotulos,
  hojeIso,
  data6du,
  datas6du,
  dias = 30,
  saldoInicial: saldoInicialProp,
}: Props30d) {
  const { data: saldoData } = useSaldoV2();
  // Prefere o saldo resolvido pelo caller (mesma base do KPI da page); cai no
  // hook interno apenas quando a prop nao chega (testes/uso standalone).
  // 31/08: o fallback tambem COMPOE (disponivel + compromissada ok) — mesma
  // regra da page, senao standalone divergiria da tela principal.
  const saldoInicial =
    saldoInicialProp ?? composicaoAncoraFluxo(saldoData).total ?? 0;

  const rows = useMemo(
    () => computeRows30d(series, diario, hojeIso, saldoInicial, dias),
    [series, diario, hojeIso, saldoInicial, dias],
  );

  return (
    <_CardShell
      titulo={`Fluxo de Caixa — próximos ${dias} dias`}
      subtitulo={subtituloFluxo30d(rotulos ?? ROTULOS_ESPELHO_OMIE)}
      // #149: cp_caveat e fato da SERIE -> aviso de subestimacao no card (uma
      // linha + icone), nao no tooltip. Sem caveat (espelho Omie) -> sem linha.
      aviso={rotulos?.saidasCaveat ?? null}
      legenda={<_Legenda30d />}
    >
      <_Card30d
        rows={rows}
        data6du={data6du ?? null}
        datas6du={datas6du}
        hojeIso={hojeIso}
        dias={dias}
        rotulos={rotulos}
      />
      {/* Nota #10: a queda da linha completa perto de fev/27 e' o FIM do book
          de atacado 2026 (forward 2027+ em formacao) — comportamento esperado,
          nao achatar/suavizar. Visivel em qualquer janela pra Mesa nao ler a
          inclinacao como bug. */}
      <p className="text-[11px] text-white/40 pt-2 px-1" data-testid="fluxo-nota-book">
        Nota: a projeção de energia enfraquece após fev/27 — fim do book de
        atacado 2026 (forward 2027+ em formação). Queda esperada, não é bug.
      </p>
    </_CardShell>
  );
}

interface Props6m {
  mensal: PontoFluxoMensal[];
  mtmByMonth?: Record<string, { mtm: number }>;
  /** Saldo de partida — mesma base do SaldoProjetadoTable (projecaoSaldo6Meses)
   *  pra os 2 conciliarem. Omitido → useSaldoV2 interno (`?? 0`). */
  saldoInicial?: number;
}

/** Grafico 6 meses — WATERFALL mensal, ancorado no saldo_total_atual real
 *  (Omie via useSaldoV2). Cada degrau inclui Posicao Aberta MtM no cumulativo
 *  pra alinhar com a tabela "Saldo projetado 6 meses" (fix #171).
 */
export function FluxoBarChart6m({ mensal, mtmByMonth, saldoInicial: saldoInicialProp }: Props6m) {
  const { data: saldoData } = useSaldoV2();
  // 31/08: fallback composto (disponivel + compromissada ok) — paridade com a page.
  const saldoInicial =
    saldoInicialProp ?? composicaoAncoraFluxo(saldoData).total ?? 0;

  const rows = useMemo(
    () => _calc6m(mensal, saldoInicial, mtmByMonth),
    [mensal, saldoInicial, mtmByMonth],
  );

  return (
    <_CardShell
      titulo="Fluxo de Caixa — próximos 6 meses"
      subtitulo="Waterfall mensal · inclui Posição Aberta MtM"
      legenda={<_Legenda6m />}
    >
      <_Card6m rows={rows} saldoInicial={saldoInicial} />
    </_CardShell>
  );
}
